import type { IntelligenceSummary, Project, User } from '~/common/types';

export const UNKNOWN = '';

export type TIntelligenceQuery = {
  citygrid: GeoJSON.Feature[];
  projectIntelligence?: IntelligenceV2[];
  layerConfig?: { [key: string]: GeoJSON.FeatureCollection };
  summary?: IntelligenceSummary;
};

export enum IntelligenceSectionTitle {
  None = 'None',
  NearbyProjects = 'Nearby Projects & Paving Moratoria',
  JurisdictionalIntersections = 'Jurisdictional Intersections',
  RightOfWay = 'Rights-of-Way & Road Crossings',
  PhysicalInfrastructure = 'Physical Infrastructure',
}

export const ownershipMap: { [key: number]: string } = {
  1: 'State Highway Agency',
  2: 'County Highway Agency',
  3: 'Town or Township Highway Agency',
  4: 'City or Municipal Highway Agency',
  11: 'State Park, Forest, or Reservation Agency',
  12: 'Local Park, Forest, or Reservation Agency',
  21: 'Other State Agency',
  25: 'Other Local Agency',
  26: 'Private (other than Railroad)',
  27: 'Railroad',
  31: 'State Toll Authority',
  32: 'Local Toll Authority',
  40: 'Other Public Instrumentality (e.g., Airport, School, University)',
  50: 'Indian Tribe Nation',
  60: 'Other Federal Agency',
  62: 'Bureau of Indian Affairs',
  63: 'Bureau of Fish and Wildlife',
  64: 'U.S. Forest Service',
  66: 'National Park Service',
  67: 'Tennessee Valley Authority',
  68: 'Bureau of Land Management',
  69: 'Bureau of Reclamation',
  70: 'Corps of Engineers',
  72: 'Air Force',
  73: 'Navy/Marines',
  74: 'Army',
  80: 'Other',
};

export const cityKeys = [3, 4];
export const countyKeys = [2];
export const stateKeys = [1];

export enum IntelligenceStatus {
  NotReviewed = 'notReviewed',
  InReview = 'inReview',
  Coordinating = 'coordinating',
  NeedsAttention = 'needsAttention',
  ResolvedCoordinated = 'resolvedCoordinated',
  ResolvedIgnored = 'resolvedIgnored',
}

export enum IntelligenceType {
  PlannedProject = 'plannedProject',
  PavingProject = 'pavingProject',
  PavingMoratoria = 'pavingMoratoria',
  Rail = 'rail',
  Transit = 'transit',
  Road = 'road',
  City = 'city',
  County = 'county',
  StateDOT = 'stateDOT',
  Hydrology = 'hydrology',
  Bridges = 'bridges',
  CivicBuildings = 'civicBuildings',
  EnergyInfrastructure = 'energyInfrastructure',
}

export const StatusLabels: { [key: string]: string } = {
  [IntelligenceStatus.Coordinating]: 'Coordinating',
  [IntelligenceStatus.InReview]: 'In Review',
  [IntelligenceStatus.NeedsAttention]: 'Needs Attention',
  [IntelligenceStatus.NotReviewed]: 'Not Yet Reviewed',
  [IntelligenceStatus.ResolvedCoordinated]: 'Resolved - Coordinated',
  [IntelligenceStatus.ResolvedIgnored]: 'Resolved - Ignored',
};

export interface Comment {
  id: string;
  createdBy: User;
  updatedBy: User;
  comment: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectIntelligence {
  _id: string;
  featureId: string;
  ownerId: string;
  project: string;
  color: string;
  reviewStatus: IntelligenceStatus;
  type: IntelligenceType;
  comments?: Comment[];
  layerId?: string;
}

export interface IntelligenceV2 {
  id: string;
  layerId: string;
  project: Project['id'];
  rgb: [number, number, number];
  reviewStatus: IntelligenceStatus;
  comments: Comment[];
  geometry: GeoJSON.Geometry;
  properties: { [key: string]: any };
  lastSyncedAt: Date;
  createdBy: User['id'];
  updatedBy: User['id'];
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date;
  status: 'active' | 'archived';
}

export const NO_CONFLICTS = 'None Found';
export const PENDING = 'See Details';
export const NOT_AVAILABLE = 'Not Available';

export function getColorFromRgb([r, g, b]: [number, number, number]): string {
  return `rgb(${r}, ${g}, ${b})`;
}
